export const BRANDING_STORAGE_KEY = 'nixia_branding';

export const DEFAULT_BRANDING = Object.freeze({
  appName: 'NSIA Assurance Vie',
  sidebarColor: '#001E60',
  headerColor: '#005780',
  btnColor: '#CF8225',
  logoUrl: '/Logo-NSIA-Vie-Assurances.png',
  logoCollapsed: '/Logo-NSIA-Vie-Assurances.png',
});

function normalizeHex(input, fallback) {
  if (!input || typeof input !== 'string') return fallback;
  const v = input.trim();
  if (!v) return fallback;
  const hex = v.startsWith('#') ? v : `#${v}`;
  if (/^#[0-9a-fA-F]{6}$/.test(hex)) return hex.toUpperCase();
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    const h = hex.slice(1).split('').map((c) => c + c).join('');
    return `#${h}`.toUpperCase();
  }
  return fallback;
}

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return {
    r: parseInt(f.slice(0, 2), 16),
    g: parseInt(f.slice(2, 4), 16),
    b: parseInt(f.slice(4, 6), 16),
  };
}

function rgba(hex, a) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}

function luminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function autoOnAccentText(bgHex) {
  return luminance(bgHex) > 145 ? '#0B0B0B' : '#FFFFFF';
}

function sidebarGrad(hex) {
  return `linear-gradient(180deg, ${hex} 0%, ${rgba(hex, 0.92)} 100%)`;
}

function darken(hex, amt = 0.15) {
  const { r, g, b } = hexToRgb(hex);
  return `rgb(${Math.max(0, Math.round(r * (1 - amt)))},${Math.max(
    0,
    Math.round(g * (1 - amt))
  )},${Math.max(0, Math.round(b * (1 - amt)))})`;
}

export function sanitizeBranding(input) {
  const safe = { ...(input || {}) };
  return {
    appName:
      typeof safe.appName === 'string' && safe.appName.trim()
        ? safe.appName.trim()
        : DEFAULT_BRANDING.appName,
    sidebarColor: normalizeHex(safe.sidebarColor, DEFAULT_BRANDING.sidebarColor),
    headerColor: normalizeHex(safe.headerColor, DEFAULT_BRANDING.headerColor),
    btnColor: normalizeHex(safe.btnColor, DEFAULT_BRANDING.btnColor),
    logoUrl: typeof safe.logoUrl === 'string' && safe.logoUrl.trim() ? safe.logoUrl.trim() : null,
    logoCollapsed:
      typeof safe.logoCollapsed === 'string' && safe.logoCollapsed.trim()
        ? safe.logoCollapsed.trim()
        : null,
  };
}

export function buildUiTokens(brandingRaw, theme) {
  const branding = sanitizeBranding(brandingRaw);
  const isLight = theme === 'light';

  const sidebarHex = branding.sidebarColor;
  const headerHex = branding.headerColor;
  const btnHex = branding.btnColor;
  const isDarkSidebar = luminance(sidebarHex) < 140;
  const isVeryLightSidebar = luminance(sidebarHex) > 228;

  const blue = headerHex;
  const blueSoft = rgba(headerHex, 0.1);
  const blueBorder = rgba(headerHex, isLight ? 0.18 : 0.16);
  const blueHover = rgba(headerHex, 0.22);
  const active = rgba(headerHex, isLight ? 0.14 : 0.16);
  const activeBorder = rgba(headerHex, 0.24);
  const divider = rgba(headerHex, isLight ? 0.14 : 0.12);
  const text3 = isLight ? 'rgba(0,0,0,0.55)' : rgba(headerHex, 0.65);
  const headerBg = rgba(headerHex, isLight ? 0.06 : 0.08);
  const gradientBar = `linear-gradient(180deg,${headerHex},${rgba(headerHex, 0.55)})`;
  const gradientLine = `linear-gradient(90deg,transparent,${rgba(headerHex, 0.22)},transparent)`;

  const btnSoft = rgba(btnHex, 0.1);
  const btnBorder = rgba(btnHex, 0.22);

  const bg = isLight ? 'transparent' : `color-mix(in srgb, ${sidebarHex} 25%, #07101e)`;
  const card = isLight
    ? 'rgba(255,255,255,0.92)'
    : `color-mix(in srgb, ${sidebarHex} 35%, #0d1525)`;
  const cardHi = isLight
    ? 'rgba(255,255,255,0.98)'
    : `color-mix(in srgb, ${sidebarHex} 45%, #101e30)`;

  const inputBg = rgba(headerHex, isLight ? 0.05 : 0.06);
  const selectBg = rgba(headerHex, isLight ? 0.08 : 0.10);
  const optionBg = isLight ? '#eef5ff' : `color-mix(in srgb, ${sidebarHex} 45%, #101e30)`;

  const shadow = isLight ? `0 4px 14px ${rgba(headerHex, 0.12)}` : '0 4px 20px rgba(0,0,0,0.28)';
  const shadowLg = isLight
    ? `0 8px 32px ${rgba(headerHex, 0.15)}`
    : '0 8px 32px rgba(0,0,0,0.45)';

  const text1 = isLight ? '#000000' : '#E8F2FF';
  const text2 = isLight ? 'rgba(0,0,0,0.70)' : 'rgba(180,210,245,0.88)';

  const headerIconBg = isLight ? rgba(headerHex, 0.12) : `linear-gradient(135deg,${rgba(headerHex, 0.22)},${rgba(headerHex, 0.35)})`;

  const green = isLight ? '#2e7d32' : '#BAF09D';
  const greenBg = isLight ? 'rgba(46,125,50,0.08)' : 'rgba(186,240,157,0.10)';
  const teal = isLight ? '#0d7d6e' : '#60c9b8';
  const cyan = isLight ? '#0288d1' : '#38c4e8';
  const amber = isLight ? '#b45309' : '#f5a623';
  const red = isLight ? '#dc2626' : '#f07070';

  const avatarPalette = isLight ? [headerHex, teal, green, cyan, '#1a5fa8', '#1565c0'] : [headerHex, teal, '#BAF09D', cyan, '#5aaaf0', '#1c5faf'];

  return {
    isLight,
    isDarkSidebar,
    isVeryLightSidebar,
    appName: branding.appName,
    logoUrl: branding.logoUrl,
    logoCollapsed: branding.logoCollapsed,
    sidebarBg: sidebarHex,
    headerColor: headerHex,
    btnColor: btnHex,

    blue,
    blueSoft,
    blueBorder,
    blueHover,
    active,
    activeBorder,
    divider,
    text3,
    headerBg,
    gradientBar,
    gradientLine,

    btnSoft,
    btnBorder,

    bg,
    card,
    cardHi,
    inputBg,
    selectBg,
    optionBg,

    shadow,
    shadowLg,

    text1,
    text2,
    headerIconBg,

    green,
    greenBg,
    teal,
    cyan,
    amber,
    red,

    avatarPalette,
    rowBg: rgba(headerHex, 0.04),
  };
}

export function buildBrandingCss(brandingRaw, theme) {
  const branding = sanitizeBranding(brandingRaw);
  const isLight = theme === 'light';

  const sidebar = branding.sidebarColor;
  const header = branding.headerColor;
  const btn = branding.btnColor;

  const onAccent = autoOnAccentText(header);
  const sidebarText1 = autoOnAccentText(sidebar);

  const bgPrimary = isLight ? 'var(--bg-primary, #f5f9ff)' : '#07101e';
  const bgSurface = isLight ? 'rgba(255,255,255,0.85)' : `color-mix(in srgb, ${sidebar} 28%, #0b1322)`;
  const bgCard = isLight ? 'rgba(255,255,255,0.92)' : `color-mix(in srgb, ${sidebar} 35%, #0d1525)`;
  const bgCardHi = isLight ? 'rgba(255,255,255,0.98)' : `color-mix(in srgb, ${sidebar} 45%, #101e30)`;

  const h08 = rgba(header, 0.08);
  const h10 = rgba(header, 0.1);
  const h14 = rgba(header, 0.14);
  const h22 = rgba(header, 0.22);
  const h28 = rgba(header, 0.28);
  const selectBg = rgba(header, isLight ? 0.08 : 0.10);
  const optionBg = isLight ? '#eef5ff' : `color-mix(in srgb, ${sidebar} 45%, #101e30)`;

  return `
:root{
  --Nixia-blue: ${header};
  --Nixia-blue-dark: ${rgba(header, 0.85)};
  --Nixia-green: ${isLight ? '#2e7d32' : '#BAF09D'};

  --background: ${bgPrimary};
  --surface: ${bgSurface};

  --sidebar-bg: ${sidebarGrad(sidebar)};
  --sidebar-border: ${rgba(sidebar, isLight ? 0.14 : 0.18)};
  --header-bg: ${rgba(header, isLight ? 0.06 : 0.08)};
  --header-border: ${rgba(header, isLight ? 0.18 : 0.14)};

  --accent-primary: ${header};
  --accent-primary-hover: ${darken(header, 0.12)};
  --accent-soft: ${h10};
  --accent-soft-border: ${h22};

  --text-primary: ${isLight ? '#000000' : '#E8F2FF'};
  --text-secondary: ${isLight ? 'rgba(0,0,0,0.70)' : 'rgba(180,210,245,0.88)'};
  --text-muted: ${isLight ? 'rgba(0,0,0,0.55)' : rgba(header, 0.65)};
  --text-on-accent: ${onAccent};

  --btn-primary: ${btn};
  --btn-on-primary: ${autoOnAccentText(btn)};

  --card-bg: ${bgCard};
  --card-bg-hi: ${bgCardHi};
  --select-bg: ${selectBg};
  --option-bg: ${optionBg};
  --border-default: ${h14};
  --border-strong: ${h28};

  /* Compat variables used in various legacy modules */
  --gs-blue: ${header};
  --gs-bg: ${bgPrimary};
  --gs-card: ${bgCard};
  --gs-border: ${h14};
  --bg: ${bgPrimary};
  --surface-2: ${bgCard};
  --surface-3: ${bgCardHi};
  --nixia: ${header};
  --border: ${h14};

  --blue: ${header};
  --blue-soft: ${h14};
}

body{ background: var(--background) !important; color: var(--text-primary) !important; }
a{ color: var(--accent-primary); }

/* Sidebar text helpers */
.sidebar, aside, nav.sidebar { color: ${sidebarText1}; }
`;
}
